import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { Btn, Card } from "../components/ui";

/** THE SCREEN INSTEAD OF THE DEVELOPER ERROR (2026-09-22). React Router's default is a stack trace
 *  addressed to "Hey developer" -- not something to show someone running a campaign. Two cases:
 *  the app was updated while this tab sat open (say so, and reload), and everything else. */
export function RouteError() {
  const err = useRouteError();
  // A PAGE THAT ISN'T THERE IS NOT AN ERROR (2026-10-30). An address with no page behind it -- a typo,
  // an old bookmark -- reaches this screen as React Router's "not found" response, which is an object
  // with no `message`, so it printed "Something went wrong" over a box reading [object Object]. It now
  // says the page doesn't exist and offers the way back.
  if (isRouteErrorResponse(err) && err.status === 404) return (
    <div className="p-6 flex justify-center">
      <Card className="max-w-lg w-full"><div className="p-4 flex flex-col gap-3">
        <span className="font-extrabold">That page doesn't exist</span>
        <p className="text-sm text-muted">The address may have a typo, or the page has moved. Everything is still on the dashboard.</p>
        <div className="flex gap-2">
          <Btn onClick={() => { window.location.href = "/"; }}>Go to the dashboard</Btn>
        </div>
      </div></Card>
    </div>
  );
  const msg = describe(err);
  const stale = /dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg);
  return (
    <div className="p-6 flex justify-center">
      <Card className="max-w-lg w-full"><div className="p-4 flex flex-col gap-3">
        <span className="font-extrabold">{stale ? "Orator was updated" : "Something went wrong"}</span>
        <p className="text-sm text-muted">
          {stale
            ? "This tab was open while a new version went out, so the page it wanted is gone. Reloading picks up the new version — nothing you entered on other screens is affected."
            : "This page couldn't load. Reloading usually clears it. If it keeps happening, tell us what you were doing and we'll fix it."}
        </p>
        {!stale && msg && <div className="text-xs text-muted bg-surface-2 border border-border rounded-lg p-2 break-words">{msg}</div>}
        <div className="flex gap-2">
          <Btn onClick={() => window.location.reload()}>Reload</Btn>
          <Btn variant="ghost" onClick={() => { window.location.href = "/"; }}>Go to the dashboard</Btn>
        </div>
      </div></Card>
    </div>
  );
}

/** Something a person can read, whatever was thrown: a message, a response's status, or nothing --
 *  never "[object Object]". */
function describe(err: unknown): string {
  if (!err) return "";
  if (isRouteErrorResponse(err)) return `${err.status}${err.statusText ? ` ${err.statusText}` : ""}`;
  if (typeof err === "string") return err;
  const m = (err as { message?: unknown }).message;
  if (typeof m === "string") return m;
  return "";
}
